/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as AuthenticationActionCreators from '@app/actions/AuthenticationActionCreators';
import {VerificationResult} from '@app/actions/AuthenticationActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import * as ToastActionCreators from '@app/actions/ToastActionCreators';
import * as UserActionCreators from '@app/actions/UserActionCreators';
import {ExternalLink} from '@app/components/common/ExternalLink';
import {Form} from '@app/components/form/Form';
import {Input} from '@app/components/form/Input';
import {Select} from '@app/components/form/Select';
import * as Modal from '@app/components/modals/Modal';
import {
	type RequiredActionFlow,
	resolveRequiredActionFlow,
	type VerificationMode,
} from '@app/components/modals/RequiredActionFlow';
import styles from '@app/components/modals/RequiredActionModal.module.css';
import {Button} from '@app/components/uikit/button/Button';
import FocusRing from '@app/components/uikit/focus_ring/FocusRing';
import {
	COUNTRY_CODES,
	type CountryCode,
	formatPhoneNumber,
	getCountryName,
	getDefaultCountry,
	getE164PhoneNumber,
} from '@app/data/CountryCodes';
import {useFormSubmit} from '@app/hooks/useFormSubmit';
import DeveloperOptionsStore from '@app/stores/DeveloperOptionsStore';
import LayerManager from '@app/stores/LayerManager';
import UserStore from '@app/stores/UserStore';
import * as EmojiUtils from '@app/utils/EmojiUtils';
import * as LocaleUtils from '@app/utils/LocaleUtils';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {WarningIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {components, type FilterOptionOption, type OptionProps, type SingleValueProps} from 'react-select';

interface PhoneFormInputs {
	phoneNumber: string;
}
interface CodeFormInputs {
	code: string;
}

interface NewEmailFormInputs {
	newEmail: string;
}

interface CountrySelectOption {
	value: string;
	label: string;
	country: CountryCode;
}

const getCountryOptions = (locale: string): ReadonlyArray<CountrySelectOption> =>
	COUNTRY_CODES.map((country) => ({
		value: country.code,
		label: `${getCountryName(country.code, locale)} (${country.dialCode})`,
		country,
	}));

const CountryOption = observer((props: OptionProps<CountrySelectOption>) => {
	const {country} = props.data;
	const locale = LocaleUtils.getCurrentLocale();
	const countryName = getCountryName(country.code, locale);
	return (
		<components.Option {...props}>
			<div className={styles.countryOption}>
				<img src={EmojiUtils.getEmojiURL(country.flag) ?? undefined} alt={countryName} className={styles.countryFlag} />
				<span>{countryName}</span>
				<span className={styles.countryDialCode}>({country.dialCode})</span>
			</div>
		</components.Option>
	);
});

const SingleValue = observer((props: SingleValueProps<CountrySelectOption>) => {
	const {country} = props.data;
	const locale = LocaleUtils.getCurrentLocale();
	const countryName = getCountryName(country.code, locale);
	return (
		<components.SingleValue {...props}>
			<div className={styles.countryValue}>
				<img src={EmojiUtils.getEmojiURL(country.flag) ?? undefined} alt={countryName} className={styles.countryFlag} />
				<span>{country.dialCode}</span>
			</div>
		</components.SingleValue>
	);
});

type PhoneVerificationStep = 'phone' | 'code' | 'inbound_waiting';
type EmailRecoveryStep = 'instructions' | 'new_email' | 'code';
interface VerificationTab {
	id: 'email' | 'phone';
	label: string;
}

const getTitleDescriptor = (mode: VerificationMode, reverify = false, emailBounced = false): MessageDescriptor => {
	switch (mode) {
		case 'email':
			if (emailBounced) {
				return msg`Email Update Required`;
			}
			return reverify ? msg`Email Re-Verification Required` : msg`Email Verification Required`;
		case 'phone':
			return reverify ? msg`Phone Re-Verification Required` : msg`Phone Verification Required`;
		case 'email_or_phone':
			return reverify ? msg`Email or Phone Re-Verification Required` : msg`Account Verification Required`;
	}
};

const getDescriptionDescriptor = (
	mode: VerificationMode,
	reverify = false,
	emailBounced = false,
): MessageDescriptor => {
	switch (mode) {
		case 'email':
			if (emailBounced) {
				return msg`Your current email address couldn't receive messages. Please update it to continue using Fluxer.`;
			}
			return reverify
				? msg`We've detected suspicious activity on your account. Please reverify your email address to continue using Fluxer.`
				: msg`We've detected suspicious activity on your account. Please verify your email address to continue using Fluxer.`;
		case 'phone':
			return reverify
				? msg`We've detected suspicious activity on your account. Please reverify your phone number to continue using Fluxer.`
				: msg`We've detected suspicious activity on your account. Please verify your phone number to continue using Fluxer.`;
		case 'email_or_phone':
			return reverify
				? msg`We've detected suspicious activity on your account. Please reverify your email address or phone number to continue using Fluxer.`
				: msg`We've detected suspicious activity on your account. Please verify your email address or phone number to continue using Fluxer.`;
	}
};

const RequiredActionModal: React.FC<{mock?: boolean}> = observer(({mock = false}) => {
	const {t} = useLingui();
	const user = UserStore.currentUser;
	const liveRequiredActionFlow = useMemo(() => resolveRequiredActionFlow(user?.requiredActions), [user?.requiredActions]);

	const [selectedVerificationType, setSelectedVerificationType] = useState<'email' | 'phone'>(
		DeveloperOptionsStore.mockRequiredActionsSelectedTab,
	);
	const [phoneVerificationStep, setPhoneVerificationStep] = useState<PhoneVerificationStep>(
		DeveloperOptionsStore.mockRequiredActionsPhoneStep,
	);
	const [emailRecoveryStep, setEmailRecoveryStep] = useState<EmailRecoveryStep>('instructions');
	const [emailRecoveryTicket, setEmailRecoveryTicket] = useState<string | null>(null);
	const [emailRecoveryProof, setEmailRecoveryProof] = useState<string | null>(null);
	const [emailRecoveryRecipient, setEmailRecoveryRecipient] = useState<string | null>(null);
	const [bouncedEmailStep, setBouncedEmailStep] = useState<'new_email' | 'code'>('new_email');
	const [bouncedEmailTicket, setBouncedEmailTicket] = useState<string | null>(null);
	const [bouncedEmailRecipient, setBouncedEmailRecipient] = useState<string | null>(null);
	const [isResendingEmail, setIsResendingEmail] = useState(false);
	const [isResendingEmailRecoveryCode, setIsResendingEmailRecoveryCode] = useState(false);
	const [isResendingBouncedEmailCode, setIsResendingBouncedEmailCode] = useState(false);
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const [selectedCountry, setSelectedCountry] = useState<CountryCode>(getDefaultCountry());
	const [phoneNumber, setPhoneNumber] = useState('');
	const [formattedPhone, setFormattedPhone] = useState('');
	const [inboundChallengeCode, setInboundChallengeCode] = useState<string | null>(null);
	const [inboundOurNumber, setInboundOurNumber] = useState<string | null>(null);
	const [isStartingInbound, setIsStartingInbound] = useState(false);
	const phoneForm = useForm<PhoneFormInputs>();
	const codeForm = useForm<CodeFormInputs>();
	const emailRecoveryForm = useForm<NewEmailFormInputs>();
	const emailRecoveryCodeForm = useForm<CodeFormInputs>();
	const bouncedEmailForm = useForm<NewEmailFormInputs>();
	const bouncedEmailCodeForm = useForm<CodeFormInputs>();
	const locale = LocaleUtils.getCurrentLocale();
	const countryOptions = getCountryOptions(locale);
	const isEmailBounced = !mock && !!user?.emailBounced;
	const [renderedFlow, setRenderedFlow] = useState<RequiredActionFlow | null>(liveRequiredActionFlow);

	const mockFlow: RequiredActionFlow | null = mock
		? {
				actions: [],
				key: `mock:${DeveloperOptionsStore.mockRequiredActionsMode}:${DeveloperOptionsStore.mockRequiredActionsReverify}`,
				mode: DeveloperOptionsStore.mockRequiredActionsMode,
				reverify: DeveloperOptionsStore.mockRequiredActionsReverify,
				requiresInboundPhone: false,
			}
		: null;
	const effectiveFlow = mockFlow ?? liveRequiredActionFlow ?? renderedFlow;

	// Dismiss the modal when required actions are cleared (e.g. after
	// successful phone verification). This runs as a useEffect (not during
	// render) to avoid a synchronous render cascade through MobX → Modal
	// → AnimatePresence that triggers React error #300.
	useEffect(() => {
		if (!mock && !liveRequiredActionFlow) {
			ModalActionCreators.popWithKey('required-actions');
		}
	}, [liveRequiredActionFlow, mock]);

	useEffect(() => {
		if (!mock && liveRequiredActionFlow) {
			setRenderedFlow(liveRequiredActionFlow);
		}
	}, [liveRequiredActionFlow, mock]);

	const effectiveMode: VerificationMode = effectiveFlow?.mode ?? 'email';

	const verificationTabs = useMemo<Array<VerificationTab>>(
		() => [
			{id: 'email', label: t`Email`},
			{id: 'phone', label: t`Phone`},
		],
		[t],
	);

	const isReverify = effectiveFlow?.reverify ?? false;
	const requiresInboundPhone = effectiveFlow?.requiresInboundPhone ?? false;
	const showBouncedEmailFlow =
		isEmailBounced &&
		(effectiveMode === 'email' || (effectiveMode === 'email_or_phone' && selectedVerificationType === 'email'));
	const canSelfServeEmailRecovery = !mock && !isEmailBounced && user?.verified !== true;
	const canUsePhoneRecovery = effectiveMode === 'email_or_phone';
	const showEmailRecoveryOptions =
		!showBouncedEmailFlow &&
		(effectiveMode === 'email' || (effectiveMode === 'email_or_phone' && selectedVerificationType === 'email'));

	useEffect(() => {
		const formatted = formatPhoneNumber(phoneNumber, selectedCountry);
		setFormattedPhone(formatted);
	}, [phoneNumber, selectedCountry]);

	useEffect(() => {
		if (!mock) {
			LayerManager.closeAll();
		}
	}, [mock]);

	useEffect(() => {
		if (mock) {
			setSelectedVerificationType(DeveloperOptionsStore.mockRequiredActionsSelectedTab);
			setPhoneVerificationStep(DeveloperOptionsStore.mockRequiredActionsPhoneStep);
		} else if (effectiveFlow) {
			setSelectedVerificationType('email');
			setPhoneVerificationStep('phone');
			setEmailRecoveryStep('instructions');
			setEmailRecoveryTicket(null);
			setEmailRecoveryProof(null);
			setEmailRecoveryRecipient(null);
			setBouncedEmailStep('new_email');
			setBouncedEmailTicket(null);
			setBouncedEmailRecipient(null);
			setIsResendingEmail(false);
			setIsResendingEmailRecoveryCode(false);
			setIsResendingBouncedEmailCode(false);
			setPhoneNumber('');
			setFormattedPhone('');
			phoneForm.reset();
			codeForm.reset();
			emailRecoveryForm.reset();
			emailRecoveryCodeForm.reset();
			bouncedEmailForm.reset();
			bouncedEmailCodeForm.reset();
		}
	}, [
		mock,
		effectiveFlow?.key,
		phoneForm,
		codeForm,
		emailRecoveryForm,
		emailRecoveryCodeForm,
		bouncedEmailForm,
		bouncedEmailCodeForm,
	]);

	const onSubmitPhone = useCallback(async () => {
		if (requiresInboundPhone) {
			setIsStartingInbound(true);
			try {
				const challenge = await UserActionCreators.startInboundPhoneChallenge();
				setInboundChallengeCode(challenge.challenge_code);
				setInboundOurNumber(challenge.our_number);
				setPhoneVerificationStep('inbound_waiting');
			} finally {
				setIsStartingInbound(false);
			}
			return;
		}
		if (!phoneNumber) {
			phoneForm.setError('phoneNumber', {message: t`Phone number is required`});
			return;
		}
		const e164Phone = getE164PhoneNumber(phoneNumber, selectedCountry);
		await UserActionCreators.sendPhoneVerification(e164Phone);
		setPhoneVerificationStep('code');
	}, [requiresInboundPhone, phoneForm, phoneNumber, selectedCountry, t]);

	const onSubmitCode = useCallback(
		async (data: CodeFormInputs) => {
			const e164Phone = getE164PhoneNumber(phoneNumber, selectedCountry);
			const {phone_token} = await UserActionCreators.verifyPhone(e164Phone, data.code.split(' ').join(''));
			await UserActionCreators.addPhone(phone_token);
			ToastActionCreators.createToast({type: 'success', children: <Trans>Phone number verified</Trans>});
		},
		[phoneNumber, selectedCountry],
	);

	const onSubmitBouncedEmail = useCallback(
		async (data: NewEmailFormInputs) => {
			const result = await UserActionCreators.requestBouncedEmailChangeNew(data.newEmail);
			setBouncedEmailTicket(result.ticket);
			setBouncedEmailRecipient(result.new_email);
			setBouncedEmailStep('code');
			bouncedEmailCodeForm.reset();
			ToastActionCreators.success(t`Verification code sent. Check your new email inbox.`);
		},
		[bouncedEmailCodeForm, t],
	);

	const onSubmitBouncedEmailCode = useCallback(
		async (data: CodeFormInputs) => {
			if (!bouncedEmailTicket) {
				bouncedEmailCodeForm.setError('code', {message: t`Please request a verification code first.`});
				return;
			}

			const updatedUser = await UserActionCreators.verifyBouncedEmailChangeNew(
				bouncedEmailTicket,
				data.code.split(' ').join(''),
			);
			UserStore.handleUserUpdate(updatedUser, {clearMissingOptionalFields: true});
			ToastActionCreators.success(t`Your email address has been updated.`);
		},
		[bouncedEmailCodeForm, bouncedEmailTicket, t],
	);

	const onSubmitEmailRecovery = useCallback(
		async (data: NewEmailFormInputs) => {
			let activeTicket = emailRecoveryTicket;
			let activeProof = emailRecoveryProof;

			if (!activeTicket || !activeProof) {
				const startResult = await UserActionCreators.startEmailChange();
				if (startResult.require_original || !startResult.original_proof) {
					throw new Error(
						t`You need access to your current email to change it from here. Use phone verification if available or contact support.`,
					);
				}
				activeTicket = startResult.ticket;
				activeProof = startResult.original_proof;
				setEmailRecoveryTicket(activeTicket);
				setEmailRecoveryProof(activeProof);
			}

			const result = await UserActionCreators.requestEmailChangeNew(activeTicket, data.newEmail, activeProof);
			setEmailRecoveryTicket(result.ticket);
			setEmailRecoveryRecipient(result.new_email);
			setEmailRecoveryStep('code');
			emailRecoveryCodeForm.reset();
			ToastActionCreators.success(t`Verification code sent. Check your new email inbox.`);
		},
		[emailRecoveryCodeForm, emailRecoveryProof, emailRecoveryTicket, t],
	);

	const onSubmitEmailRecoveryCode = useCallback(
		async (data: CodeFormInputs) => {
			if (!emailRecoveryTicket || !emailRecoveryProof) {
				emailRecoveryCodeForm.setError('code', {message: t`Please request a verification code first.`});
				return;
			}

			const {email_token} = await UserActionCreators.verifyEmailChangeNew(
				emailRecoveryTicket,
				data.code.split(' ').join(''),
				emailRecoveryProof,
			);
			const updatedUser = await UserActionCreators.update({email_token});
			UserStore.handleUserUpdate(updatedUser, {clearMissingOptionalFields: true});
			ToastActionCreators.success(t`Your email address has been updated.`);
		},
		[emailRecoveryCodeForm, emailRecoveryProof, emailRecoveryTicket, t],
	);

	const {handleSubmit: handlePhoneSubmit, isSubmitting: isPhoneSubmitting} = useFormSubmit({
		form: phoneForm,
		onSubmit: onSubmitPhone,
		defaultErrorField: 'phoneNumber',
	});

	const {handleSubmit: handleCodeSubmit, isSubmitting: isCodeSubmitting} = useFormSubmit({
		form: codeForm,
		onSubmit: onSubmitCode,
		defaultErrorField: 'code',
	});

	const {handleSubmit: handleBouncedEmailSubmit, isSubmitting: isBouncedEmailSubmitting} = useFormSubmit({
		form: bouncedEmailForm,
		onSubmit: onSubmitBouncedEmail,
		defaultErrorField: 'newEmail',
	});

	const {handleSubmit: handleEmailRecoverySubmit, isSubmitting: isEmailRecoverySubmitting} = useFormSubmit({
		form: emailRecoveryForm,
		onSubmit: onSubmitEmailRecovery,
		defaultErrorField: 'newEmail',
	});

	const {handleSubmit: handleEmailRecoveryCodeSubmit, isSubmitting: isEmailRecoveryCodeSubmitting} = useFormSubmit({
		form: emailRecoveryCodeForm,
		onSubmit: onSubmitEmailRecoveryCode,
		defaultErrorField: 'code',
	});

	const {handleSubmit: handleBouncedEmailCodeSubmit, isSubmitting: isBouncedEmailCodeSubmitting} = useFormSubmit({
		form: bouncedEmailCodeForm,
		onSubmit: onSubmitBouncedEmailCode,
		defaultErrorField: 'code',
	});

	const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const digitsOnly = value.replace(/\D/g, '');
		setPhoneNumber(digitsOnly);
	};

	const handleResendEmail = async () => {
		if (mock) {
			const outcome = DeveloperOptionsStore.mockRequiredActionsResendOutcome;
			switch (outcome) {
				case 'success':
					ToastActionCreators.success(t`Verification email sent! Please check your inbox.`);
					break;
				case 'rate_limited':
					ToastActionCreators.error(t`Too many requests. Please try again later.`);
					break;
				case 'server_error':
					ToastActionCreators.error(t`Failed to send verification email. Please try again later.`);
					break;
			}
			return;
		}
		if (isResendingEmail) return;
		setIsResendingEmail(true);
		const result = await AuthenticationActionCreators.resendVerificationEmail();
		switch (result) {
			case VerificationResult.SUCCESS:
				ToastActionCreators.success(t`Verification email sent! Please check your inbox.`);
				break;
			case VerificationResult.RATE_LIMITED:
				ToastActionCreators.error(t`Too many requests. Please try again later.`);
				break;
			case VerificationResult.SERVER_ERROR:
				ToastActionCreators.error(t`Failed to send verification email. Please try again later.`);
				break;
		}
		setIsResendingEmail(false);
	};

	const isEmailResending = mock ? DeveloperOptionsStore.mockRequiredActionsResending : isResendingEmail;

	const handleResendBouncedEmailCode = useCallback(async () => {
		if (!bouncedEmailTicket || isResendingBouncedEmailCode) {
			return;
		}

		setIsResendingBouncedEmailCode(true);
		try {
			await UserActionCreators.resendBouncedEmailChangeNew(bouncedEmailTicket);
			ToastActionCreators.success(t`A new verification code has been sent.`);
		} catch {
			ToastActionCreators.error(t`Failed to resend verification code. Please try again later.`);
		} finally {
			setIsResendingBouncedEmailCode(false);
		}
	}, [bouncedEmailTicket, isResendingBouncedEmailCode, t]);

	const handleResendEmailRecoveryCode = useCallback(async () => {
		if (!emailRecoveryTicket || isResendingEmailRecoveryCode) {
			return;
		}

		setIsResendingEmailRecoveryCode(true);
		try {
			await UserActionCreators.resendEmailChangeNew(emailRecoveryTicket);
			ToastActionCreators.success(t`A new verification code has been sent.`);
		} catch {
			ToastActionCreators.error(t`Failed to resend verification code. Please try again later.`);
		} finally {
			setIsResendingEmailRecoveryCode(false);
		}
	}, [emailRecoveryTicket, isResendingEmailRecoveryCode, t]);

	const handleLogout = useCallback(async () => {
		if (isLoggingOut) {
			return;
		}
		setIsLoggingOut(true);
		try {
			await AuthenticationActionCreators.logout();
		} finally {
			setIsLoggingOut(false);
		}
	}, [isLoggingOut]);

	// When required actions have been cleared, keep rendering the current view
	// until the useEffect above pops this modal from the stack. Returning null
	// here would cause AnimatePresence to synchronously unmount during a MobX
	// observer re-render, triggering React error #300.

	const renderBackdrop = useMemo(() => {
		return (
			<div
				style={{
					position: 'absolute',
					inset: 0,
					backgroundColor: 'hsl(0deg 0% 0%)',
					opacity: 0.95,
					backdropFilter: 'blur(12px)',
					WebkitBackdropFilter: 'blur(12px)',
					pointerEvents: mock ? 'auto' : 'none',
				}}
			/>
		);
	}, [mock]);

	return (
		<Modal.Root
			size="small"
			centered
			onClose={mock ? () => ModalActionCreators.pop() : undefined}
			backdropSlot={renderBackdrop}
		>
			<Modal.ScreenReaderLabel text={t`Account Verification Required`} />
			<Modal.Content>
				<div className={styles.container}>
					<div className={styles.header}>
						<div className={styles.iconContainer}>
							<WarningIcon weight="fill" className={styles.icon} />
						</div>
						<h2 className={styles.title}>{t(getTitleDescriptor(effectiveMode, isReverify, isEmailBounced))}</h2>
						<p className={styles.description}>
							{t(getDescriptionDescriptor(effectiveMode, isReverify, isEmailBounced))}
						</p>
					</div>

					{effectiveMode === 'email_or_phone' && (
						<div className={styles.tabContainer}>
							{verificationTabs.map((tab) => (
								<FocusRing key={tab.id} offset={-2}>
									<button
										type="button"
										className={clsx(
											styles.tabButton,
											selectedVerificationType === tab.id ? styles.tabActive : styles.tabInactive,
										)}
										onClick={() => setSelectedVerificationType(tab.id)}
										aria-pressed={selectedVerificationType === tab.id}
									>
										{tab.label}
									</button>
								</FocusRing>
							))}
						</div>
					)}

					<div className={styles.contentContainer}>
						{(effectiveMode === 'email' ||
							(effectiveMode === 'email_or_phone' && selectedVerificationType === 'email')) &&
							(showBouncedEmailFlow ? (
								<>
									<div className={styles.stepsCard}>
										<p className={styles.stepText}>
											<Trans>
												Your current email <strong className={styles.selectable}>{user?.email}</strong> could not receive messages. Enter a different
												email address to continue.
											</Trans>
										</p>
									</div>

									{bouncedEmailStep === 'new_email' ? (
										<Form form={bouncedEmailForm} onSubmit={handleBouncedEmailSubmit}>
											<div className={styles.formContainer}>
												<Input
													{...bouncedEmailForm.register('newEmail')}
													autoComplete="email"
													autoFocus={true}
													error={bouncedEmailForm.formState.errors.newEmail?.message}
													label={t`New Email`}
													placeholder={t`name@example.com`}
													required={true}
													type="email"
													footer={
														<p className={styles.footerText}>
															<Trans>We'll send a verification code to this email address.</Trans>
														</p>
													}
												/>

												<Button type="submit" submitting={isBouncedEmailSubmitting}>
													<Trans>Send Code</Trans>
												</Button>
											</div>
										</Form>
									) : (
										<Form form={bouncedEmailCodeForm} onSubmit={handleBouncedEmailCodeSubmit}>
											<div className={styles.formContainer}>
												<Input
													{...bouncedEmailCodeForm.register('code')}
													autoComplete="one-time-code"
													autoFocus={true}
													error={bouncedEmailCodeForm.formState.errors.code?.message}
													label={t`Verification Code`}
													required={true}
													footer={
														<p className={styles.footerText}>
															<Trans>
																Enter the verification code sent to{' '}
																<span className={styles.selectable}>{bouncedEmailRecipient ?? user?.email ?? t`your new email`}</span>.
															</Trans>
														</p>
													}
												/>

												<div className={styles.buttonGroup}>
													<Button type="submit" submitting={isBouncedEmailCodeSubmitting}>
														<Trans>Update Email</Trans>
													</Button>
													<Button
														variant="secondary"
														onClick={handleResendBouncedEmailCode}
														disabled={isBouncedEmailCodeSubmitting || isResendingBouncedEmailCode}
														submitting={isResendingBouncedEmailCode}
													>
														<Trans>Resend Code</Trans>
													</Button>
													<Button
														variant="secondary"
														onClick={() => {
															setBouncedEmailStep('new_email');
															setBouncedEmailTicket(null);
															setBouncedEmailRecipient(null);
															bouncedEmailCodeForm.reset();
														}}
														disabled={isBouncedEmailCodeSubmitting}
													>
														<Trans>Use Different Email</Trans>
													</Button>
												</div>
											</div>
										</Form>
									)}
								</>
							) : (
								<>
									<div className={styles.stepsCard}>
										<div className={styles.stepsContainer}>
											<div className={styles.stepRow}>
												<div className={styles.stepBadge}>1</div>
												<p className={styles.stepText}>
													<Trans>
														Check your inbox at <strong className={styles.selectable}>{user?.email}</strong> for a verification email.
													</Trans>
												</p>
											</div>
											<div className={styles.stepRow}>
												<div className={styles.stepBadge}>2</div>
												<p className={styles.stepText}>
													<Trans>Click the verification link in the email.</Trans>
												</p>
											</div>
											<div className={styles.stepRow}>
												<div className={styles.stepBadge}>3</div>
												<p className={styles.stepText}>
													<Trans>Return to this page to continue.</Trans>
												</p>
											</div>
										</div>
									</div>

									<div className={styles.resendSection}>
										<p className={styles.resendText}>
											<Trans>Didn't receive the email?</Trans>
										</p>
										<Button onClick={handleResendEmail} disabled={isEmailResending} submitting={isEmailResending}>
											<Trans>Resend Verification Email</Trans>
										</Button>
									</div>

									{showEmailRecoveryOptions && (
										<div className={styles.stepsCard}>
											<div className={styles.formContainer}>
												{canSelfServeEmailRecovery ? (
													emailRecoveryStep === 'instructions' ? (
														<>
															<p className={styles.stepText}>
																<Trans>
																	If you no longer have access to this inbox, you can switch your account to a different
																	email address from here.
																</Trans>
															</p>
															<div className={styles.buttonGroup}>
																<Button onClick={() => setEmailRecoveryStep('new_email')} variant="secondary">
																	<Trans>Use Different Email</Trans>
																</Button>
																{canUsePhoneRecovery && (
																	<Button
																		onClick={() => {
																			setSelectedVerificationType('phone');
																			setEmailRecoveryStep('instructions');
																		}}
																		variant="secondary"
																	>
																		<Trans>Use Phone Instead</Trans>
																	</Button>
																)}
															</div>
														</>
													) : emailRecoveryStep === 'new_email' ? (
														<Form form={emailRecoveryForm} onSubmit={handleEmailRecoverySubmit}>
															<div className={styles.formContainer}>
																<Input
																	{...emailRecoveryForm.register('newEmail')}
																	autoComplete="email"
																	autoFocus={true}
																	error={emailRecoveryForm.formState.errors.newEmail?.message}
																	label={t`New Email`}
																	placeholder={t`name@example.com`}
																	required={true}
																	type="email"
																	footer={
																		<p className={styles.footerText}>
																			<Trans>We'll send a verification code to this email address.</Trans>
																		</p>
																	}
																/>

																<div className={styles.buttonGroup}>
																	<Button type="submit" submitting={isEmailRecoverySubmitting}>
																		<Trans>Send Code</Trans>
																	</Button>
																	<Button
																		type="button"
																		variant="secondary"
																		onClick={() => {
																			setEmailRecoveryStep('instructions');
																			emailRecoveryForm.reset();
																		}}
																		disabled={isEmailRecoverySubmitting}
																	>
																		<Trans>Back</Trans>
																	</Button>
																</div>
															</div>
														</Form>
													) : (
														<Form form={emailRecoveryCodeForm} onSubmit={handleEmailRecoveryCodeSubmit}>
															<div className={styles.formContainer}>
																<Input
																	{...emailRecoveryCodeForm.register('code')}
																	autoComplete="one-time-code"
																	autoFocus={true}
																	error={emailRecoveryCodeForm.formState.errors.code?.message}
																	label={t`Verification Code`}
																	required={true}
																	footer={
																		<p className={styles.footerText}>
																			<Trans>
																				Enter the verification code sent to{' '}
																				<span className={styles.selectable}>{emailRecoveryRecipient ?? t`your new email`}</span>.
																			</Trans>
																		</p>
																	}
																/>

																<div className={styles.buttonGroup}>
																	<Button type="submit" submitting={isEmailRecoveryCodeSubmitting}>
																		<Trans>Update Email</Trans>
																	</Button>
																	<Button
																		type="button"
																		variant="secondary"
																		onClick={handleResendEmailRecoveryCode}
																		disabled={
																			isEmailRecoveryCodeSubmitting || isResendingEmailRecoveryCode
																		}
																		submitting={isResendingEmailRecoveryCode}
																	>
																		<Trans>Resend Code</Trans>
																	</Button>
																	<Button
																		type="button"
																		variant="secondary"
																		onClick={() => {
																			setEmailRecoveryStep('new_email');
																			setEmailRecoveryRecipient(null);
																			emailRecoveryCodeForm.reset();
																		}}
																		disabled={isEmailRecoveryCodeSubmitting}
																	>
																		<Trans>Use Different Email</Trans>
																	</Button>
																</div>
															</div>
														</Form>
													)
												) : (
													<>
														<p className={styles.stepText}>
															{canUsePhoneRecovery ? (
																<Trans>
																	If you can't access this inbox anymore, switch to phone verification above or contact{' '}
																	<ExternalLink href="mailto:support@fluxer.app">support@fluxer.app</ExternalLink> so we can
																	help recover your account.
																</Trans>
															) : (
																<Trans>
																	If you can't access this inbox anymore, contact{' '}
																	<ExternalLink href="mailto:support@fluxer.app">support@fluxer.app</ExternalLink> so we can
																	help recover your account.
																</Trans>
															)}
														</p>
														{canUsePhoneRecovery && (
															<div className={styles.buttonGroup}>
																<Button
																	onClick={() => setSelectedVerificationType('phone')}
																	variant="secondary"
																>
																	<Trans>Use Phone Instead</Trans>
																</Button>
															</div>
														)}
													</>
												)}

												<p className={styles.footerText}>
													<Trans>
														If you no longer have access to any verification method, contact{' '}
														<ExternalLink href="mailto:support@fluxer.app">support@fluxer.app</ExternalLink>.
													</Trans>
												</p>
											</div>
										</div>
									)}
								</>
							))}

						{(effectiveMode === 'phone' ||
							(effectiveMode === 'email_or_phone' && selectedVerificationType === 'phone')) &&
							(requiresInboundPhone ? (
								phoneVerificationStep === 'inbound_waiting' && inboundChallengeCode && inboundOurNumber ? (
									<div className={styles.formContainer}>
										<div className={styles.stepsCard}>
											<div className={styles.stepsContainer}>
												<div className={styles.stepRow}>
													<div className={styles.stepBadge}>1</div>
													<p className={styles.stepText}>
														<Trans>
															Open your phone's messaging app and create a new text message.
														</Trans>
													</p>
												</div>
												<div className={styles.stepRow}>
													<div className={styles.stepBadge}>2</div>
													<p className={styles.stepText}>
														<Trans>
															Send the code <strong className={styles.selectable}>{inboundChallengeCode}</strong> to{' '}
															<strong className={styles.selectable}>{inboundOurNumber}</strong>
														</Trans>
													</p>
												</div>
												<div className={styles.stepRow}>
													<div className={styles.stepBadge}>3</div>
													<p className={styles.stepText}>
														<Trans>Wait a moment — this page will update automatically once we receive your message.</Trans>
													</p>
												</div>
											</div>
										</div>
										<p className={styles.footerText}>
											<Trans>
												Standard messaging rates from your carrier may apply. Your phone number will be saved to your
												account for verification purposes.
											</Trans>
										</p>
										<Button
											variant="secondary"
											onClick={async () => {
												setIsStartingInbound(true);
												try {
													const challenge = await UserActionCreators.startInboundPhoneChallenge();
													setInboundChallengeCode(challenge.challenge_code);
													setInboundOurNumber(challenge.our_number);
												} finally {
													setIsStartingInbound(false);
												}
											}}
											submitting={isStartingInbound}
										>
											<Trans>Get New Code</Trans>
										</Button>
									</div>
								) : (
									<div className={styles.formContainer}>
										<div className={styles.stepsCard}>
											<p className={styles.stepText}>
												<Trans>
													To verify your account, you'll need to send a text message from your phone. Tap the button
													below to get started.
												</Trans>
											</p>
										</div>
										<Button onClick={onSubmitPhone} submitting={isStartingInbound || isPhoneSubmitting}>
											<Trans>Start Verification</Trans>
										</Button>
									</div>
								)
							) : phoneVerificationStep === 'phone' ? (
								<Form form={phoneForm} onSubmit={handlePhoneSubmit}>
									<div className={styles.phoneInputContainer}>
										<div className={styles.inputGroup}>
											<Select
												label={t`Country`}
												value={selectedCountry.code}
												onChange={(value) => {
													const country = countryOptions.find((o) => o.value === value)?.country;
													if (country) {
														setSelectedCountry(country);
														setPhoneNumber('');
														setFormattedPhone('');
													}
												}}
												options={countryOptions}
												components={{
													Option: CountryOption as React.ComponentType<OptionProps<CountrySelectOption>>,
													SingleValue: SingleValue as React.ComponentType<SingleValueProps<CountrySelectOption>>,
												}}
												placeholder={t`Search countries...`}
												filterOption={(option: FilterOptionOption<CountrySelectOption>, inputValue: string) => {
													const searchTerm = inputValue.toLowerCase();
													const countryName = getCountryName(option.data.country.code, locale);
													return (
														countryName.toLowerCase().includes(searchTerm) ||
														option.data.country.dialCode.includes(searchTerm) ||
														option.data.country.code.toLowerCase().includes(searchTerm)
													);
												}}
											/>
										</div>

										<Input
											{...phoneForm.register('phoneNumber')}
											autoComplete="tel"
											autoFocus={true}
											value={formattedPhone}
											onChange={handlePhoneInput}
											error={phoneForm.formState.errors.phoneNumber?.message}
											label={t`Phone Number`}
											placeholder={selectedCountry.format || '##########'}
											required={true}
											footer={
												<p className={styles.footerText}>
													<Trans>Enter your phone number. We'll send you a verification code via SMS.</Trans>
												</p>
											}
										/>

										<Button type="submit" submitting={isPhoneSubmitting}>
											<Trans>Send Code</Trans>
										</Button>
									</div>
								</Form>
							) : (
								<Form form={codeForm} onSubmit={handleCodeSubmit}>
									<div className={styles.formContainer}>
										<Input
											{...codeForm.register('code')}
											autoComplete="one-time-code"
											autoFocus={true}
											error={codeForm.formState.errors.code?.message}
											label={t`Verification Code`}
											required={true}
											footer={
												<p className={styles.footerText}>
													<Trans>
														Enter the 6-digit code sent to <span className={styles.selectable}>{getE164PhoneNumber(phoneNumber, selectedCountry)}</span>.
													</Trans>
												</p>
											}
										/>

										<div className={styles.buttonGroup}>
											<Button type="submit" submitting={isCodeSubmitting}>
												<Trans>Verify</Trans>
											</Button>
											<Button onClick={() => setPhoneVerificationStep('phone')} variant="secondary">
												<Trans>Back</Trans>
											</Button>
										</div>
									</div>
								</Form>
							))}
					</div>
				</div>
			</Modal.Content>
			<Modal.Footer>
				{mock && (
					<Button variant="secondary" onClick={() => ModalActionCreators.pop()} disabled={isLoggingOut}>
						<Trans>Dismiss</Trans>
					</Button>
				)}
				<Button variant="danger-secondary" onClick={handleLogout} submitting={isLoggingOut}>
					<Trans>Log Out</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});

export default RequiredActionModal;
